import {
  Button,
  Stack,
  Box,
  Slide,
  Slider,
  styled,
  CircularProgress,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";

import { tease } from "../components/CheckButton";
import {
  Color,
  ErrorCode,
  FontStyle,
  MediaQuery,
  navPaddingTop,
  jobPageSizing,
} from "../constants";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { StyledTextField } from "../components/AutocompleteUtils";
import * as apiService from "../service/ApiService";
import { useDispatch, useSelector } from "react-redux";
import ExpChip from "../components/ExpChip";
import ScrollJobs from "../components/JobPanels/ScrollJobs";
import JobDetails from "../components/JobPanels/JobDetails";
import JobFavToggle from "../components/JobPanels/JobFavToggle";
import JobFiltersBar from "../components/JobPanels/JobFiltersBar";
import JobResourceVid, {
  defaultVidHeightOverWidth,
} from "../components/JobPanels/JobResourceVid";
import JobBlurbButton from "../components/JobPanels/JobBlurbButton";
import JobResourceHeader from "../components/JobPanels/JobResourceHeader";
import FloatingPlayer from "../components/FloatingPlayer";
import ResumeResource from "../components/JobPanels/ResourcePanels/ResumeResource";
import InterviewResource from "../components/JobPanels/ResourcePanels/InterviewResource";
import CoverLetterResource from "../components/JobPanels/ResourcePanels/CoverLetterResource";
import LinkedinResource from "../components/JobPanels/ResourcePanels/LinkedinResource";
import {
  defaultFilters,
  JobFloatScreen,
  LoaderOrError,
} from "../utils/jobUtils";
import {
  JobOuterContainer,
  JobBg,
  JobInnerContainer,
  JobInnerColumn,
} from "../components/JobPanels/jobLayoutElements";
import DescriptionOverlay, {
  OverlayNames,
} from "../components/JobPanels/DescriptionOverlay";
import JobStackWrapper, {
  GrowWrapper,
} from "../components/JobPanels/JobStackWrapper";
import JobTutorialWindow from "../components/JobPanels/JobTutorial/JobTutorialWindow";
import { userFinishedTutorial } from "../service/CookieService";
import JobLoader from "../components/Virtual/JobLoader";
import { loadedYtScript } from "../utils/actionUtils";
import JobVerticalVideoAd from "../components/JobPanels/JobVerticalVideoAd";
import ThirdColumnVideoAdWrapper from "../components/JobPanels/ThirdColumnVideoAdWrapper";

const styles = {
  outer: {
    paddingTop: navPaddingTop,
  },
};

const sizingStyles = jobPageSizing;
const vidHeight = 300 * defaultVidHeightOverWidth;
const vidSrcs = [
  // "QmNH_5Qfgp4",
  "HzxbkYcJt4A",
  "YQeRZQWpYH4",
  "YH51XO8tLYw",
  "q-jFCfPRZhk",
  "dbROBUBUJkE",
  "dujwqCAkfMI",
  "l2xOdcKRuZQ",
];
const resourceFuncs = [
  ResumeResource,
  InterviewResource,
  CoverLetterResource,
  LinkedinResource,
];

// linkedin display modes:
// 0: mobile - 1 column, feed only < 768px
// 1: 2 column, < 992px
// 2: 3 column small, < 1200px
// 3: 3 column large, > 1200px

const JobResultsPage = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const ytScriptLoaded = useSelector((state) => state.vid.ytScriptLoaded);
  const loggedIn =
    auth.accountData.loginToken !== null &&
    auth.accountData.contactInfo !== null;
  const mobile0 = useMediaQuery({ query: MediaQuery.mobile0 });
  const desktop1 = useMediaQuery({ query: MediaQuery.desktop1 });
  const desktop2 = useMediaQuery({ query: MediaQuery.desktop2 });
  const screenSizing = mobile0 ? 0 : desktop1 ? 1 : desktop2 ? 2 : 3;
  const [selectedIdx, setSelectedIdx] = React.useState(!mobile0 ? 0 : null); // index of selected job
  const [focusMode, setFocusMode] = useState(false);
  const [numInfiniteVids, setNumInfiniteVids] = useState(0);
  const [fullVidSrc, setFullVidSrc] = useState(null);
  const [showDescriptionOverlay, setShowDescriptionOverlay] = useState("");
  const [tutorialSteps, setTutorialSteps] = useState(0);
  const [tutSizes, setTutSizes] = useState({
    2: null,
    3: null,
    4: null,
  });

  // search stuff, shared between mobile and desktop "JobFiltersBar" component
  const [searchOpen, setSearchOpen] = useState(false);
  // deep copy with stringify
  const [searchFilters, setSearchFilters] = useState(
    JSON.parse(JSON.stringify(defaultFilters))
  );
  const [searchActiveFilters, setSearchActiveFilters] = useState(
    JSON.parse(JSON.stringify(defaultFilters))
  );
  const [searchBarHeight, setSearchBarHeight] = useState(0);

  const vidHeightBuffer = window.innerHeight * 1.8;
  useEffect(() => {
    // start tutorial if widescreen and cookie is not set
    if (screenSizing >= 2 && !userFinishedTutorial()) {
      setTutorialSteps(1);
    }
  }, []);
  useEffect(() => {
    if (screenSizing < 2 && tutorialSteps > 0) {
      setTutorialSteps(0);
    }
  }, [screenSizing, tutorialSteps]);

  useEffect(() => {
    if (tutorialSteps === 2) {
      setShowDescriptionOverlay(OverlayNames.matches);
    } else if (tutorialSteps === 3) {
      setShowDescriptionOverlay(OverlayNames.jobDetails);
    } else if (tutorialSteps === 4) {
      setShowDescriptionOverlay(
        OverlayNames.resourceShort + OverlayNames.content
      );
    } else if (tutorialSteps === 5) {
      setShowDescriptionOverlay("all");
    } else {
      setShowDescriptionOverlay("");
    }
  }, [tutorialSteps]);

  const [filterIdx, setFilterIdx] = React.useState(0); // index of filter
  const setJobIdx = (job) => {
    console.log("here", job, selectedIdx);
    const newIdx = auth.jobs.findIndex(
      (j) => j.url === job.url && j.jobId === job.jobId
    );
    setSelectedIdx(newIdx);
  };
  // infinite videos management
  useEffect(() => {
    window.addEventListener("scroll", () => {
      const lengthScrolled = window.scrollY + vidHeightBuffer;
      if (numInfiniteVids < lengthScrolled / vidHeight) {
        setNumInfiniteVids((numInfiniteVids) =>
          Math.max(Math.floor(lengthScrolled / vidHeight), numInfiniteVids)
        );
      }
    });
  }, []);
  useEffect(() => {
    // if user turns on focus mode, reset the number of vids
    if (focusMode) {
      setNumInfiniteVids(0);
    }
  }, [focusMode]);
  const parentName = `j${focusMode ? "f" : "v"}${screenSizing}`;
  const logJobClick = ({ clickType, clickId }) => {
    apiService.jobClick(dispatch, {
      ...auth.accountData,
      clickType: parentName + "-" + clickType,
      jobId: clickId,
    });
  };
  if (auth.jobs === null)
    return (
      <Box sx={styles.outer}>
        <JobLoader />
        <Box
          sx={{
            ...FontStyle.body,
            textAlign: "center",
            px: 12,
            py: 4,
          }}>
          <LoaderOrError error={auth.error} />
        </Box>
      </Box>
    );

  return (
    <JobOuterContainer
      sx={{
        ...(tutorialSteps > 0 && {
          overflow: "hidden",
          maxHeight: "100vh",
        }),
      }}>
      <JobBg />
      {tutorialSteps > 0 && (
        <JobTutorialWindow
          setShowDescriptionOverlay={setShowDescriptionOverlay}
          setTutorialSteps={setTutorialSteps}
          tutorialSteps={tutorialSteps}
          tutSizes={tutSizes}
        />
      )}
      {fullVidSrc && <FloatingPlayer src={fullVidSrc} setSrc={setFullVidSrc} />}
      <JobInnerContainer mobile={mobile0}>
        <Stack
          // left column
          sx={{
            width: sizingStyles[screenSizing].left,
            maxWidth: sizingStyles[screenSizing].maxWidth,
            minWidth: "",
            position: "relative",
          }}
          // ref for handling column height (for tutorial)
          ref={(el) => {
            if (!el) return;
            if (tutorialSteps === 0) return;
            const rect = el.getBoundingClientRect();
            // if all keys are equal
            if (
              tutSizes[2] !== null &&
              Object.keys(tutSizes[2].rect).every(
                (k) => tutSizes[2].rect[k] === rect[k]
              ) &&
              // if window size is the same
              window.innerWidth === tutSizes[2].width &&
              window.innerHeight === tutSizes[2].height
            )
              return;
            // console.log("tut2", rect);
            setTutSizes((prev) => ({
              ...prev,
              2: { rect, width: window.innerWidth, height: window.innerHeight },
            }));
          }}
          direction="column"
          alignItems="center">
          <DescriptionOverlay visible={showDescriptionOverlay} name="matches" />
          <JobInnerColumn>
            {mobile0 && (
              <JobFiltersBar
                open={searchOpen}
                setOpen={setSearchOpen}
                filters={searchFilters}
                setFilters={setSearchFilters}
                activeFilters={searchActiveFilters}
                setActiveFilters={setSearchActiveFilters}
              />
            )}
            <JobFavToggle idx={filterIdx} setIdx={setFilterIdx} />
            <ScrollJobs
              jobs={auth.jobs}
              handleClick={setJobIdx}
              selectedJob={
                selectedIdx !== null && selectedIdx !== undefined
                  ? auth.jobs[selectedIdx]
                  : null
              }
              filterIdx={filterIdx}
              mobile={mobile0}
              showDescriptionOverlay={showDescriptionOverlay}
              searchActiveFilters={searchActiveFilters}
            />
          </JobInnerColumn>
        </Stack>
        {!mobile0 && (
          <Stack
            // middle column
            sx={{
              width: sizingStyles[screenSizing].mid,
              maxWidth: sizingStyles[screenSizing].maxWidth,
              minWidth: "",
              position: mobile0 ? "static" : "sticky",
              top: mobile0 ? "0px" : "12px",
              zIndex: mobile0 ? 0 : searchOpen ? 10 : 1,
              alignSelf: mobile0 ? "" : "flex-start", // necessary for sticky in flexbox
              // overflowY: "scroll",
              // height: 'auto',
            }}
            direction="column"
            spacing={2}
            alignItems="center">
            <JobFiltersBar
              open={searchOpen}
              setOpen={setSearchOpen}
              filters={searchFilters}
              setFilters={setSearchFilters}
              activeFilters={searchActiveFilters}
              setActiveFilters={setSearchActiveFilters}
              setSearchBarHeight={setSearchBarHeight}
            />
            {auth.jobs && selectedIdx !== null && (
              <Box
                sx={{ width: "100%" }}
                ref={(el) => {
                  if (!el) return;
                  if (tutorialSteps === 0) return;
                  const rect = el.getBoundingClientRect();
                  // if all keys are equal
                  if (
                    tutSizes[3] !== null &&
                    Object.keys(tutSizes[3].rect).every(
                      (k) => tutSizes[3].rect[k] === rect[k]
                    ) &&
                    // if window size is the same
                    window.innerWidth === tutSizes[3].width &&
                    window.innerHeight === tutSizes[3].height
                  )
                    return;
                  // console.log("tut3", rect);
                  setTutSizes((prev) => ({
                    ...prev,
                    3: {
                      rect,
                      width: window.innerWidth,
                      height: window.innerHeight,
                    },
                  }));
                }}>
                <JobDetails
                  job={auth.jobs[selectedIdx]}
                  mobile={mobile0}
                  focusMode={focusMode}
                  useWindowHeight={!mobile0}
                  showDescriptionOverlay={showDescriptionOverlay}
                  upperElementsHeight={searchBarHeight}
                  parentName={parentName}
                  showPortraitAd={screenSizing <= 1}
                />
              </Box>
            )}
          </Stack>
        )}
        {screenSizing > 1 && (
          <Stack
            sx={{
              width: sizingStyles[screenSizing].right,
              maxWidth: sizingStyles[screenSizing].maxWidth,
              // minWidth: "300px",
            }}
            direction="column"
            spacing={2}
            alignItems="center">
            <ThirdColumnVideoAdWrapper
              auth={auth}
              selectedIdx={selectedIdx}
              mobile={mobile0}>
              <JobResourceHeader
                checked={focusMode}
                setChecked={setFocusMode}
                large={screenSizing > 2}
                setShowDescriptionOverlay={setShowDescriptionOverlay}
              />
              {/* {auth.jobs && selectedIdx !== null && (
                <JobVerticalVideoAd
                  job={auth.jobs[selectedIdx]}
                  mobile={mobile0}
                />
              )} */}
              <JobResourceVid
                src="QmNH_5Qfgp4"
                mainSrc={fullVidSrc}
                setMainSrc={setFullVidSrc}
                active={!focusMode}
                spacing={2}
                showDescriptionOverlay={showDescriptionOverlay}
                setYtScriptLoaded={loadedYtScript(dispatch)}
                ytScriptLoaded={ytScriptLoaded}
                tutSizes={tutSizes}
                setTutSizes={setTutSizes}
                logClick={logJobClick}
                ref={(el) => {
                  if (!el) return;
                  if (tutorialSteps === 0) return;
                  const rect = el.getBoundingClientRect();
                  // if all keys are equal
                  if (
                    tutSizes[4] !== null &&
                    Object.keys(tutSizes[3].rect).every(
                      (k) => tutSizes[4].rect[k] === rect[k]
                    ) &&
                    // if window size is the same
                    window.innerWidth === tutSizes[3].width &&
                    window.innerHeight === tutSizes[3].height
                  )
                    return;
                  // console.log("tut4real", rect);
                  setTutSizes((prev) => ({
                    ...prev,
                    4: {
                      rect,
                      width: window.innerWidth,
                      height: window.innerHeight,
                    },
                  }));
                }}
              />
              {!focusMode && (
                <ResumeResource
                  job={auth.jobs[selectedIdx]}
                  fullHeight
                  showDescriptionOverlay={showDescriptionOverlay}
                />
              )}
              {!focusMode &&
                vidSrcs.map((src, idx) => {
                  // written this way so active logic below makes sense
                  if (!(idx < numInfiniteVids)) return null;
                  return (
                    <React.Fragment key={idx}>
                      <JobResourceVid
                        src={src}
                        mainSrc={fullVidSrc}
                        setMainSrc={setFullVidSrc}
                        active={!focusMode}
                        spacing={2}
                        showDescriptionOverlay={showDescriptionOverlay}
                        ytScriptLoaded={ytScriptLoaded}
                        logClick={logJobClick}
                      />
                      {idx === 0 && (
                        <InterviewResource
                          job={auth.jobs[selectedIdx]}
                          fullHeight
                          showDescriptionOverlay={showDescriptionOverlay}
                        />
                      )}
                      {idx === 1 && (
                        <CoverLetterResource
                          job={auth.jobs[selectedIdx]}
                          fullHeight
                          showDescriptionOverlay={showDescriptionOverlay}
                        />
                      )}
                      {idx === 2 && (
                        <LinkedinResource
                          job={auth.jobs[selectedIdx]}
                          fullHeight
                          showDescriptionOverlay={showDescriptionOverlay}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              {focusMode && (
                <>
                  <ResumeResource
                    job={auth.jobs[selectedIdx]}
                    fullHeight
                    showDescriptionOverlay={showDescriptionOverlay}
                  />
                  <InterviewResource
                    job={auth.jobs[selectedIdx]}
                    fullHeight
                    showDescriptionOverlay={showDescriptionOverlay}
                  />
                  <CoverLetterResource
                    job={auth.jobs[selectedIdx]}
                    fullHeight
                    showDescriptionOverlay={showDescriptionOverlay}
                  />
                  <LinkedinResource
                    job={auth.jobs[selectedIdx]}
                    fullHeight
                    showDescriptionOverlay={showDescriptionOverlay}
                  />
                </>
              )}
            </ThirdColumnVideoAdWrapper>
          </Stack>
        )}
      </JobInnerContainer>
      {tutorialSteps > 0 && (
        <>
          <JobFloatScreen />
        </>
      )}
    </JobOuterContainer>
  );
};

export default JobResultsPage;
